import { Component, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BuildingCanvas } from "@/components/three/BuildingScene";
import { SectionHeading, Reveal } from "@/components/Reveal";
import { EXPLODED_LAYERS } from "@/data/content";
import { cn } from "@/lib/utils";

class CanvasGuard extends Component {
    constructor(props) { super(props); this.state = { failed: false }; }
    static getDerivedStateFromError() { return { failed: true }; }
    render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

export const ExplodedView = () => {
    const [selected, setSelected] = useState(2);
    const layer = EXPLODED_LAYERS[selected];

    return (
        <section id="exploded" data-testid="exploded-view-section" className="paper-grid relative overflow-hidden py-24 lg:py-36">
            <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
                <SectionHeading
                    id="exploded-heading"
                    overline="Transparent Construction"
                    title={<>Every layer. <span className="text-flame-dark">Explained.</span></>}
                    description="Select any construction layer to understand what it is and why it matters. This is how OMSIVO makes construction easier to understand — before you build."
                />

                <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12">
                    <div className="order-2 lg:order-1 lg:col-span-4">
                        <div className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0" role="tablist" aria-label="Construction layers">
                            {EXPLODED_LAYERS.map((l, i) => (
                                <button
                                    key={l.key}
                                    role="tab"
                                    aria-selected={selected === i}
                                    data-testid={`exploded-layer-${l.key.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={() => setSelected(i)}
                                    className={cn(
                                        "group flex shrink-0 items-center gap-4 rounded-xl border px-4 py-3 text-left transition-all duration-300 lg:w-full",
                                        selected === i
                                            ? "border-flame bg-omsivo-navy text-white shadow-lg shadow-omsivo-navy/20"
                                            : "border-omsivo-navy/10 bg-white text-omsivo-navy hover:border-flame/50"
                                    )}
                                >
                                    <span className={cn("font-mono text-[10px]", selected === i ? "text-flame" : "text-omsivo-steel")}>
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="font-display text-sm font-bold uppercase tracking-[0.12em]">{l.key}</span>
                                </button>
                            ))}
                        </div>

                        <AnimatePresence mode="wait">
                            <motion.div key={selected} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.35 }}
                                className="mt-6 rounded-2xl border border-omsivo-navy/10 bg-white p-6 shadow-sm" data-testid="exploded-layer-info">
                                <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-flame-dark">{layer.key}</p>
                                <p className="mt-3 text-sm leading-relaxed text-slate-700">{layer.text}</p>
                                <p className="mt-4 border-t border-omsivo-navy/10 pt-4 text-xs leading-relaxed text-slate-500">
                                    <span className="font-semibold uppercase tracking-wider text-omsivo-navy">Why it matters: </span>
                                    {layer.relevance}
                                </p>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <Reveal className="order-1 lg:order-2 lg:col-span-8" delay={0.1}>
                        <div className="relative h-[340px] overflow-hidden rounded-[28px] border border-white/10 bg-omsivo-deep shadow-2xl shadow-omsivo-navy/25 sm:h-[440px] lg:h-[600px]">
                            <div className="blueprint absolute inset-0" />
                            <CanvasGuard fallback={
                                <div className="relative z-10 flex h-full items-center justify-center">
                                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/60">Interactive 3D unavailable on this device</p>
                                </div>
                            }>
                                <div className="absolute inset-0">
                                    <BuildingCanvas exploded selected={selected} onSelect={setSelected} lite={false} />
                                </div>
                            </CanvasGuard>
                            <p className="pointer-events-none absolute bottom-4 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.28em] text-white/40">
                                Drag to rotate · Click a layer
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
